import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { getErrorMessage } from "../utils/errorHandler";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FolderOpen, ListMusic } from "lucide-react";
import SetlistManager from "@/components/SetlistManager";
import { AppTour, DASHBOARD_TOUR_STEPS } from "@/components/AppTour";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import usePushNotifications from "@/hooks/usePushNotifications";
import UsageWarningBanner from "@/components/UsageWarningBanner";

import DashboardHeader from "@/components/dashboard/DashboardHeader";
import QuickActionsGrid from "@/components/dashboard/QuickActionsGrid";
import FeatureCardsGrid from "@/components/dashboard/FeatureCardsGrid";
import FilterChips from "@/components/dashboard/FilterChips";
import ProjectGrid from "@/components/dashboard/ProjectGrid";
import CreateProjectDialog from "@/components/dashboard/CreateProjectDialog";
import UploadDocumentDialog from "@/components/dashboard/UploadDocumentDialog";
import AIGeneratorDialog from "@/components/dashboard/AIGeneratorDialog";
import StreamingSetupDialog from "@/components/dashboard/StreamingSetupDialog";
import ProfileDialog from "@/components/dashboard/ProfileDialog";
import PreferencesDialog from "@/components/dashboard/PreferencesDialog";
import FromVerseDialog from "@/components/dashboard/FromVerseDialog";
import RenewalReminderBanner from "@/components/dashboard/RenewalReminderBanner";
import WhatsNewPopover from "@/components/WhatsNewPopover";
import ReferralCard from "@/components/ReferralCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Categorize a project by its source for filter chips. Older projects without an
// explicit `source` field are inferred from is_song / description heuristics so
// the filter still works without a backfill migration.
function getProjectSource(project) {
  if (project.source) return project.source;
  if (project.is_song) return "songs";
  const desc = (project.description || "").toLowerCase();
  if (desc.includes("preachkit") || desc.includes("pulpit")) return "preach_kit";
  return "manual";
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, logout } = useAuth();

  // App tour
  const [runTour, setRunTour] = useState(false);

  // Data
  const [projects, setProjects] = useState([]);
  const [setlists, setSetlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("home");

  // Dialog visibility
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [textDialogOpen, setTextDialogOpen] = useState(false);
  const [streamingDialogOpen, setStreamingDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [preferencesDialogOpen, setPreferencesDialogOpen] = useState(false);
  const [fromVerseDialogOpen, setFromVerseDialogOpen] = useState(false);

  // New slideshow form
  const [newProjectName, setNewProjectName] = useState("");
  const [isSongProject, setIsSongProject] = useState(false);

  // Upload form
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMode, setUploadMode] = useState("file");
  const [fileUrl, setFileUrl] = useState("");

  // AI text generation
  const [textContent, setTextContent] = useState("");
  const [generating, setGenerating] = useState(false);
  const [isTextSong, setIsTextSong] = useState(false);

  // Profile form
  const [profileForm, setProfileForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    user_type: user?.user_type || "",
    user_subcategory: user?.user_subcategory || "",
  });
  const [upgrading, setUpgrading] = useState(false);
  const [hasPresenterAccess, setHasPresenterAccess] = useState(user?.has_presenter_access || false);

  // Side data
  const [isAdmin, setIsAdmin] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [unreadDMCount, setUnreadDMCount] = useState(0);

  const {
    isSupported: notificationsSupported,
    permission: notificationPermission,
    isEnabled: notificationsEnabled,
    soundEnabled,
    toggleNotifications,
    toggleSound,
    checkAndNotify,
  } = usePushNotifications();

  // ---------- Data fetching ----------
  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data || []);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSetlists = async () => {
    try {
      const response = await axios.get(`${API}/setlists`);
      setSetlists(response.data || []);
    } catch (error) {
      console.error("Failed to load setlists");
    }
  };

  const checkAdminStatus = useCallback(async () => {
    try {
      await axios.get(`${API}/admin/stats`);
      setIsAdmin(true);
    } catch (error) {
      setIsAdmin(false);
    }
  }, []);

  const fetchSubscription = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/current`);
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    }
  }, []);

  const fetchUnreadDMCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dm/unread-count`);
      const newCount = response.data.unread_count || 0;
      checkAndNotify(newCount);
      setUnreadDMCount(newCount);
    } catch (error) {
      console.error("Failed to fetch unread DM count:", error);
    }
  }, [checkAndNotify]);

  useEffect(() => {
    fetchProjects();
    fetchSetlists();
    checkAdminStatus();
    fetchUnreadDMCount();
    fetchSubscription();
    const interval = setInterval(fetchUnreadDMCount, 30000);
    return () => clearInterval(interval);
  }, [fetchProjects, checkAdminStatus, fetchUnreadDMCount, fetchSubscription]);

  // If the user just signed up after using the /demo page, materialize the
  // cached demo deck into a real project and drop them into the editor.
  useEffect(() => {
    const raw = localStorage.getItem("pending_demo");
    if (!raw) return;
    let payload;
    try { payload = JSON.parse(raw); } catch { localStorage.removeItem("pending_demo"); return; }
    if (!payload?.slides?.length) { localStorage.removeItem("pending_demo"); return; }

    (async () => {
      try {
        const res = await axios.post(`${API}/pulpit/send-to-editor`, {
          title: payload.title || payload.verse || "Demo Sermon",
          sermon_type: "outline",
          content: {
            title: payload.title,
            scripture_main: payload.scripture_main,
            big_idea: payload.big_idea,
            outline: payload.outline,
            slides: payload.slides,
          },
        });
        localStorage.removeItem("pending_demo");
        toast.success("Welcome! Opening your demo deck…");
        if (res.data?.project_id) navigate(`/editor/${res.data.project_id}`);
      } catch (err) {
        // Non-fatal: user still lands on dashboard, demo is dropped.
        localStorage.removeItem("pending_demo");
      }
    })();
    // Intentionally only on mount — a second pass would double-create.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Derived state ----------
  const filteredProjects = useMemo(() => {
    if (projectFilter === "all") return projects;
    return projects.filter((p) => getProjectSource(p) === projectFilter);
  }, [projects, projectFilter]);

  const filterCounts = useMemo(() => {
    const counts = { all: projects.length, manual: 0, preach_kit: 0, songs: 0 };
    for (const p of projects) {
      const s = getProjectSource(p);
      if (counts[s] !== undefined) counts[s] += 1;
    }
    return counts;
  }, [projects]);

  // ---------- Handlers ----------
  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      toast.error(t("enter_name"));
      return;
    }
    try {
      const response = await axios.post(`${API}/projects`, {
        name: newProjectName,
        is_song: isSongProject,
      });
      toast.success(t("project_created"));
      setCreateDialogOpen(false);
      setNewProjectName("");
      setIsSongProject(false);
      navigate(`/editor/${response.data.id}`);
    } catch (error) {
      if (error.response?.data?.detail?.error === "subscription_limit") {
        const detail = error.response.data.detail;
        toast.error(detail.message, {
          action: { label: t("upgrade"), onClick: () => navigate("/pricing") },
          duration: 8000,
        });
      } else {
        toast.error(t("create_failed"));
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedExtensions = [".pdf", ".docx", ".pptx", ".txt"];
      const ext = "." + file.name.split(".").pop().toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        toast.error(`Invalid file type. Allowed: ${allowedExtensions.join(", ")}`);
        return;
      }
      setUploadFile(file);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile && !fileUrl) {
      toast.error(t("select_file"));
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      let response;
      if (uploadMode === "file" && uploadFile) {
        const formData = new FormData();
        formData.append("file", uploadFile);
        response = await axios.post(`${API}/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            setUploadProgress(Math.round((e.loaded * 100) / e.total));
          },
        });
      } else {
        response = await axios.post(`${API}/upload-url`, { url: fileUrl });
      }
      toast.success(t("upload_success"));
      setUploadDialogOpen(false);
      setUploadFile(null);
      setFileUrl("");
      navigate(`/editor/${response.data.id}`);
    } catch (error) {
      toast.error(getErrorMessage(error, t("upload_failed")));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleGenerateFromText = async () => {
    if (!textContent.trim()) {
      toast.error("Please enter some text content");
      return;
    }
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/projects/generate-slides`, {
        content: textContent,
        is_song: isTextSong,
      });
      toast.success("Slides generated!");
      setTextDialogOpen(false);
      setTextContent("");
      navigate(`/editor/${response.data.id}`);
    } catch (error) {
      toast.error("Failed to generate slides");
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteProject = async (projectId, e) => {
    e.stopPropagation();
    if (!confirm(t("confirm_delete"))) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success(t("project_deleted"));
      fetchProjects();
    } catch (error) {
      toast.error(t("delete_failed"));
    }
  };

  const handleUpgradeToPresenter = async () => {
    setUpgrading(true);
    try {
      await axios.post(`${API}/auth/upgrade-to-presenter`);
      setHasPresenterAccess(true);
      toast.success(t("upgrade_success") || "Account upgraded! You now have presenter capabilities.");
    } catch (error) {
      toast.error(getErrorMessage(error, t("upgrade_failed") || "Failed to upgrade account"));
    } finally {
      setUpgrading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await axios.put(`${API}/auth/profile`, {
        name: profileForm.name,
        user_type: profileForm.user_type,
        user_subcategory: profileForm.user_subcategory,
      });
      toast.success("Profile updated!");
      setProfileDialogOpen(false);
      window.location.reload();
    } catch (error) {
      toast.error("Failed to update profile");
    }
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case "create":
        setIsSongProject(false);
        setCreateDialogOpen(true);
        break;
      case "song":
        setIsSongProject(true);
        setCreateDialogOpen(true);
        break;
      case "upload":
        setUploadDialogOpen(true);
        break;
      case "ai":
        setTextDialogOpen(true);
        break;
      case "from_verse":
        setFromVerseDialogOpen(true);
        break;
      default:
        break;
    }
  };

  const handleFeatureAction = (card) => {
    if (card.route) navigate(card.route);
    else if (card.action === "streaming") setStreamingDialogOpen(true);
  };

  const openProfileDialog = () => {
    setProfileForm({
      name: user?.name || "",
      email: user?.email || "",
      user_type: user?.user_type || "",
      user_subcategory: user?.user_subcategory || "",
    });
    setProfileDialogOpen(true);
  };

  // ---------- Render ----------
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <AppTour run={runTour} setRun={setRunTour} steps={DASHBOARD_TOUR_STEPS} />

      <DashboardHeader
        t={t}
        user={user}
        logout={logout}
        unreadDMCount={unreadDMCount}
        subscription={subscription}
        isAdmin={isAdmin}
        onProfileClick={openProfileDialog}
        onPreferencesClick={() => setPreferencesDialogOpen(true)}
        onStartTour={() => setRunTour(true)}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h2 className="text-3xl font-bold mb-2">
            {t("welcome_back")}, {user?.name?.split(" ")[0] || t("user")}!
          </h2>
          <p className="text-muted-foreground">{t("what_to_do_today")}</p>
        </motion.div>

        <UsageWarningBanner subscription={subscription} t={t} />

        <RenewalReminderBanner />

        <div className="mb-6">
          <ReferralCard />
        </div>

        <QuickActionsGrid t={t} onAction={handleQuickAction} />

        <FeatureCardsGrid t={t} onAction={handleFeatureAction} unreadDMCount={unreadDMCount} />

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="home" className="gap-2">
                <FolderOpen className="w-4 h-4" />
                {t("my_projects")} ({projects.length})
              </TabsTrigger>
              <TabsTrigger value="setlists" className="gap-2">
                <ListMusic className="w-4 h-4" />
                {t("setlists")} ({setlists.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="home">
              {projects.length > 0 && (
                <FilterChips
                  t={t}
                  value={projectFilter}
                  onChange={setProjectFilter}
                  counts={filterCounts}
                />
              )}
              <ProjectGrid
                t={t}
                loading={loading}
                projects={projects}
                filteredProjects={filteredProjects}
                onDelete={handleDeleteProject}
                onCreate={() => setCreateDialogOpen(true)}
                onClearFilter={() => setProjectFilter("all")}
                onFromVerse={() => setFromVerseDialogOpen(true)}
              />
            </TabsContent>

            <TabsContent value="setlists">
              <SetlistManager
                projects={projects}
                setlists={setlists}
                onRefresh={fetchSetlists}
              />
            </TabsContent>
          </Tabs>
        </motion.section>
      </main>

      <CreateProjectDialog
        t={t}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        name={newProjectName}
        setName={setNewProjectName}
        isSong={isSongProject}
        setIsSong={setIsSongProject}
        onCreate={handleCreateProject}
      />

      <UploadDocumentDialog
        t={t}
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        uploadMode={uploadMode}
        setUploadMode={setUploadMode}
        uploadFile={uploadFile}
        setUploadFile={setUploadFile}
        fileUrl={fileUrl}
        setFileUrl={setFileUrl}
        uploading={uploading}
        uploadProgress={uploadProgress}
        onFileSelect={handleFileSelect}
        onUpload={handleUpload}
      />

      <AIGeneratorDialog
        t={t}
        open={textDialogOpen}
        onOpenChange={setTextDialogOpen}
        textContent={textContent}
        setTextContent={setTextContent}
        isSong={isTextSong}
        setIsSong={setIsTextSong}
        generating={generating}
        onGenerate={handleGenerateFromText}
      />

      <StreamingSetupDialog
        t={t}
        open={streamingDialogOpen}
        onOpenChange={setStreamingDialogOpen}
      />

      <ProfileDialog
        t={t}
        open={profileDialogOpen}
        onOpenChange={setProfileDialogOpen}
        user={user}
        profileForm={profileForm}
        setProfileForm={setProfileForm}
        hasPresenterAccess={hasPresenterAccess}
        upgrading={upgrading}
        onUpgradeToPresenter={handleUpgradeToPresenter}
        onSave={handleSaveProfile}
      />

      <PreferencesDialog
        open={preferencesDialogOpen}
        onOpenChange={setPreferencesDialogOpen}
        notificationsSupported={notificationsSupported}
        notificationPermission={notificationPermission}
        notificationsEnabled={notificationsEnabled}
        soundEnabled={soundEnabled}
        toggleNotifications={toggleNotifications}
        toggleSound={toggleSound}
      />

      <FromVerseDialog
        t={t}
        open={fromVerseDialogOpen}
        onOpenChange={setFromVerseDialogOpen}
      />

      <WhatsNewPopover />
    </div>
  );
};

export default Dashboard;
